import React, { useState, useEffect } from "react";
import {
    Button,
    CircularProgress,
    Typography,
    Box,
    Snackbar,
    IconButton
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Close, CheckCircleOutline, ErrorOutline } from "@material-ui/icons";
import { put } from "../../client/apiHelper";
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import ConfirmDialog from "../Dialogs/ConfirmDialog";

const entityName = {
    "RiaContact": "Rep",
    "RiaInvestor": "Firm",
};

const HideEntity = ({ entityId, entityType, entityHidden, buttonProps, ...props }) => {
    const { session } = useAdminUserSession();
    const [hiddenStatus, setHiddenStatus] = useState(entityHidden);
    const [isToggling, setIsToggling] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingHideValue, setPendingHideValue] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        setHiddenStatus(entityHidden)
    }, [entityHidden]);

    const openConfirmModal = (hide) => {
        setPendingHideValue(hide);
        setConfirmOpen(true);
    };

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const handleSnackbarClose = (_, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const snackbarIcon =
        snackbar.severity === "warning" || snackbar.severity === "error"
            ? <ErrorOutline />
            : <CheckCircleOutline />;

    const togglePlatformVisibility = async () => {
        setIsToggling(true);
        try {
            const params = {
                hide: pendingHideValue,
                entity_id: entityId,
                entity_type: entityType,
            };

            const response = await put(
                "/v1/admin/hideable_entities/toggle_platform_visibility",
                session,
                params
            );

            if (response.data && response.data.status === "ok") {
                setHiddenStatus(pendingHideValue);
                showSnackbar(
                    pendingHideValue
                        ? `${entityName[entityType]} will be hidden in 15 minutes`
                        : `${entityName[entityType]} is now visible`,
                    "success"
                );
            }
            else if (response.data && response.data.status === "noop") {
                showSnackbar("Hide cancelled", "info");
                setHiddenStatus(false);
            }
            else if (response.isAxiosError) {
                const r = response.response;
                if (r.data.status === "already_scheduled") {
                    showSnackbar("A hide action is already scheduled for this firm", "warning");
                } else {
                    showSnackbar("An error occurred while toggling visibility", "error");
                }
            }
        } catch (e) {
            console.log(e);
            showSnackbar("An unexpected error occurred", "error");
        } finally {
            setIsToggling(false);
            setConfirmOpen(false);
            setPendingHideValue(null);
        }
    };

    const dialogTitle = pendingHideValue
        ? `Hide ${entityName[entityType]}`
        : `Show ${entityName[entityType]}`;

    const dialogConfirmText = pendingHideValue
        ? "Hide anyway"
        : "Show";

    return (
        <>
            {
                session.rootAccess || session.isSuperAdmin ? <>
                    {
                        isToggling ? <>
                            <Button color="default" onClick={(event) => { }} {...buttonProps} disabled>
                                <CircularProgress style={{ height: "25px", width: "25px" }} />
                            </Button>
                        </> : <>
                            {
                                hiddenStatus === true ? (
                                    <Button
                                        className="colored-button secondary"
                                        onClick={() => openConfirmModal(false)}
                                        {...buttonProps}
                                    >
                                        Show
                                    </Button>
                                ) : (
                                    <Button
                                        className="colored-button error"
                                        onClick={() => openConfirmModal(true)}
                                        {...buttonProps}
                                    >
                                        Hide
                                    </Button>
                                )
                            }
                        </>
                    }
                </> : null
            }

            <ConfirmDialog
                open={confirmOpen}
                title={dialogTitle}
                confirmText={dialogConfirmText}
                cancelText="Cancel"
                confirmColor={pendingHideValue ? "secondary" : "primary"}
                onConfirm={togglePlatformVisibility}
                onCancel={() => setConfirmOpen(false)}
                isLoading={isToggling}
            >
                {
                    pendingHideValue ? (
                        <Box>
                            <Typography variant="body1" style={{ fontWeight: 500 }} gutterBottom>
                                Hiding the {entityName[entityType]} will apply the following changes:
                            </Typography>

                            <Box mt={1}>
                                <Typography variant="body2">
                                    • Removed from global search results
                                </Typography>
                                <Typography variant="body2">
                                    • Removed from recently viewed items
                                </Typography>
                                <Typography variant="body2">
                                    • All related app notifications will be deleted
                                </Typography>
                                <Typography variant="body2">
                                    • Removed from all lists where it appears
                                </Typography>
                            </Box>

                            <Box
                                mt={2}
                                p={1.5}
                                borderRadius={4}
                                bgcolor="#fff4f4"
                                border="1px solid #f5c2c2"
                            >
                                <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: 600, color: "#d32f2f" }}
                                    gutterBottom
                                >
                                    Important
                                </Typography>

                                <Typography variant="body2">
                                    • The hide process will execute in 15 minutes
                                </Typography>
                                <Typography variant="body2">
                                    • You can revert this action within that time window
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 600, marginTop: 4 }}
                                >
                                    After 15 minutes, this action cannot be fully reverted
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body1" style={{ fontWeight: 500 }} gutterBottom>
                                Showing the {entityName[entityType]} will apply the following changes:
                            </Typography>

                            <Box mt={1}>
                                <Typography variant="body2">
                                    • The entity will become visible again on the platform
                                </Typography>
                                <Typography variant="body2">
                                    • It will reappear in applicable lists
                                </Typography>
                            </Box>
                        </Box>
                    )
                }
            </ConfirmDialog>

            <Snackbar
                open={snackbar.open}
                onClose={handleSnackbarClose}
                autoHideDuration={2500}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                action={
                    <IconButton color="inherit" onClick={handleSnackbarClose}>
                        <Close />
                    </IconButton>
                }
            >
                <MuiAlert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    icon={snackbarIcon}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </>
    );
}

export default HideEntity;